import { ApolloProvider } from "@apollo/client/react";
import { KarteUiProvider } from "@repo/karte-ui/KarteUiProvider";
import { LoadingBox, Toaster } from "@repo/ui3/components";
import { useWorkspaceIdFromRouter } from "@repo/workspace-route";
import type { AppProps } from "next/app";
import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { NuqsAdapter } from "nuqs/adapters/next/pages";
import { AuthorizedGuard } from "src/components/AuthorizedGuard";
import { NotFoundScreen } from "src/components/NotFoundScreen";
import { WorkspaceGlobalsGuard } from "src/components/WorkspaceGlobalsGuard";
import { AuthProvider } from "src/features/auth/AuthProvider";
import { KarteCsChatUnreadProvider } from "src/features/csChat/KarteCsChatUnreadProvider";
import { FeatureFlagsProvider } from "src/features/featureFlags/provider";
import { NotificationProvider } from "src/features/notification/NotificationProvider";
import { QuestionnaireResponseExportProvider } from "src/features/questionnaireResponseExport/QuestionnaireResponseExportProvider";
import { WorkspaceMark } from "src/features/workspaceMark/WorkspaceMark";
import { useApollo } from "src/hooks/useApollo";
import { KarteChakraProvider } from "src/providers/KarteChakraProvider";
import { pagesPath } from "src/utils/$path";
import { FontsLoader } from "../components/FontLoader";

import "../lib/initDayjs";
import { ImplicitEmbeddedContextProvider } from "src/components/ChatModal/EmbeddedContext/ImplicitEmbeddedContextProvider";

const APP_TITLE = "Speria AI カルテ";

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const isWorkspacePath = router.pathname.startsWith("/w/[workspaceId]");
  const { isReady, workspaceId } = useWorkspaceIdFromRouter();
  const routeWorkspaceId = isWorkspacePath ? workspaceId : null;
  const client = useApollo(routeWorkspaceId);

  return (
    <>
      <Head>
        <title>{APP_TITLE}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <ApolloProvider client={client}>
        <KarteChakraProvider>
          <KarteUiProvider link={NextLink}>
            <FontsLoader>
              <Toaster />
              <NuqsAdapter>
                {isWorkspacePath && !isReady ? (
                  <LoadingBox />
                ) : isWorkspacePath && workspaceId == null ? (
                  <NotFoundScreen />
                ) : (
                  <AppInternal
                    key={routeWorkspaceId}
                    Component={Component}
                    pageProps={pageProps}
                    workspaceId={routeWorkspaceId}
                  />
                )}
              </NuqsAdapter>
            </FontsLoader>
          </KarteUiProvider>
        </KarteChakraProvider>
      </ApolloProvider>
    </>
  );
};

const publicPaths: string[] = [
  pagesPath.$404.$url().pathname,
  pagesPath.debug.auth.$url().pathname,
];

const legacyPaths: string[] = [
  pagesPath.$url().pathname,
  pagesPath._legacyPath([]).$url().pathname,
];

// 外部から入ってくるリダイレクト用のパス（/r/{連携元}/...）。
// 遷移先の workspace はリンクのパラメータから決めるため、セッションの workspace を前提にする
// AuthorizedGuard を通さない（未選択だと workspace 選択画面に飛ばされてしまう）。
// ログインの確認は各ページが呼ぶ API の 401 で行う
const redirectPaths: string[] = [pagesPath.r.es.customer.$url().pathname];

// 認証の有無で与える Guard, Provider を切り替える
const AppInternal = ({
  Component,
  pageProps,
  workspaceId,
}: {
  Component: AppProps["Component"];
  pageProps: AppProps["pageProps"];
  workspaceId: string | null;
}) => {
  const router = useRouter();
  const isPublicPath = publicPaths.includes(router.pathname);
  const isLegacyPath = legacyPaths.includes(router.pathname);
  const isRedirectPath = redirectPaths.includes(router.pathname);

  if (isPublicPath) {
    return (
      // debugLogin で認証メソッドを使うため、 AuthProvider は必要
      <AuthProvider>
        <Component {...pageProps} />
      </AuthProvider>
    );
  }

  if (isRedirectPath) {
    return <Component {...pageProps} />;
  }

  if (isLegacyPath) {
    return (
      <AuthProvider>
        <FeatureFlagsProvider>
          <AuthorizedGuard workspaceId={null}>
            <Component {...pageProps} />
            <WorkspaceMark appTitle={APP_TITLE} />
          </AuthorizedGuard>
        </FeatureFlagsProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <FeatureFlagsProvider>
        <AuthorizedGuard workspaceId={workspaceId}>
          <WorkspaceGlobalsGuard>
            <NotificationProvider>
              <KarteCsChatUnreadProvider>
                <QuestionnaireResponseExportProvider>
                  <ImplicitEmbeddedContextProvider>
                    <Component {...pageProps} />
                  </ImplicitEmbeddedContextProvider>
                </QuestionnaireResponseExportProvider>
              </KarteCsChatUnreadProvider>
            </NotificationProvider>
          </WorkspaceGlobalsGuard>
          <WorkspaceMark appTitle={APP_TITLE} />
        </AuthorizedGuard>
      </FeatureFlagsProvider>
    </AuthProvider>
  );
};

export default App;
